import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from '../../../../components/modal/Modal';
import Button from '../../../../components/button/Button';
import Dropdown from '../../../../components/dropdown/Dropdown';
import CustomDatePicker from '../../../../components/customDatePicker/CustomDatePicker';
import styles from './AllocationFormModal.module.scss';
import LoadingOverlay from '../../../../components/loadingOverlay/LoadingOverlay';
import ProfilePicture from '../../../../components/profilePicture/ProfilePicture';
import Icon from '../../../../components/icon/Icon';

const AllocationFormModal = ({
  isCreateMode,
  allocation,
  initialDate,
  student,
  allSpecialties = [],
  allClinicalSites = [],
  allInstructors = [],
  isLoading,
  onClose,
  onSubmit
}) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedClinicalSite, setSelectedClinicalSite] = useState(null);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (allocation) {
      setSelectedSpecialty(allocation.speciality?.id ?? null);
      setSelectedClinicalSite(allocation.clinicalSite?.id ?? null);
      setSelectedInstructor(allocation.instructor?.id ?? null);
      setSelectedDate(allocation.date ? new Date(allocation.date) : (initialDate ? new Date(initialDate) : null));
    } else {
      setSelectedSpecialty(null);
      setSelectedClinicalSite(null);
      setSelectedInstructor(null);
      setSelectedDate(initialDate ? new Date(initialDate) : null);
    }
    setErrors({});
  }, [isCreateMode, allocation, initialDate]);

  const specialtyOptions = allSpecialties.map(s => ({ value: s.id, label: s.name || s.speciality }));
  const clinicalSiteOptions = allClinicalSites.map(c => ({ value: c.id, label: c.name }));
  const instructorOptions = allInstructors.map(i => ({ value: i.id, label: `${i.firstName} ${i.lastName}` }));

  const handleSubmit = () => {
    const newErrors = {};
    if (!selectedDate) newErrors.date = 'Date is required';
    if (!selectedSpecialty) newErrors.specialty = 'Specialty is required';
    if (!selectedClinicalSite) newErrors.clinicalSite = 'Clinical Site is required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const dateStr = selectedDate.toISOString().split('T')[0];
    onSubmit({ specialityId: selectedSpecialty, clinicalSiteId: selectedClinicalSite, instructorId: selectedInstructor, date: dateStr });
  };

  return (
    <Modal overflowVisible>
      {isLoading && <LoadingOverlay />}
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.studentInfo}>
            <ProfilePicture
              firstName={student?.firstName}
              lastName={student?.lastName}
              src={student?.profilePicture}
              size="xsm"
            />
            <h2 className={styles.studentName}>{student ? `${student.firstName} ${student.lastName}` : ''}</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <Icon name="plus" />
          </button>
        </div>
        <div className={styles.divider} />
        <div className={styles.form}>
          <CustomDatePicker
            label="Date"
            date={selectedDate}
            onChange={(val) => { setSelectedDate(val); setErrors(prev => ({ ...prev, date: null })); }}
            error={errors.date}
          />
          <Dropdown
            label="Specialty"
            options={specialtyOptions}
            value={selectedSpecialty}
            onChange={(val) => { setSelectedSpecialty(val); setErrors(prev => ({ ...prev, specialty: null })); }}
            size="small"
            error={errors.specialty}
            searchable
          />
          <Dropdown
            label="Clinical Site"
            options={clinicalSiteOptions}
            value={selectedClinicalSite}
            onChange={(val) => { setSelectedClinicalSite(val); setErrors(prev => ({ ...prev, clinicalSite: null })); }}
            size="small"
            error={errors.clinicalSite}
            searchable
          />
          <Dropdown
            label="Instructor"
            options={instructorOptions}
            value={selectedInstructor}
            onChange={setSelectedInstructor}
            size="small"
            searchable
          />
        </div>
        <div className={styles.actions}>
          <Button label="Cancel" variant="cancel" size="small" onClick={onClose} />
          <Button
            label={isCreateMode ? 'Create Allocation' : 'Update Allocation'}
            variant="primary"
            size="small"
            onClick={handleSubmit}
            isLoading={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

AllocationFormModal.propTypes = {
  isCreateMode: PropTypes.bool,
  allocation: PropTypes.shape({
    id: PropTypes.number,
    date: PropTypes.string,
    speciality: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    clinicalSite: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    instructor: PropTypes.shape({ id: PropTypes.number, firstName: PropTypes.string, lastName: PropTypes.string })
  }),
  initialDate: PropTypes.string,
  allSpecialties: PropTypes.array,
  allClinicalSites: PropTypes.array,
  allInstructors: PropTypes.array,
  student: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    profilePicture: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

export default AllocationFormModal;
